import type { ComposerMode } from './landing-composer-config';

const PENDING_OPTIMIZATION_KEY =
  'grok-imagine:landing-composer:pending-optimization';
const PENDING_OPTIMIZATION_MAX_AGE_MS = 10 * 60 * 1000;
const LANDING_AUTH_RETURN_MAX_AGE_MS = 30 * 60 * 1000;
const LANDING_AUTH_RETURN_STORAGE_PREFIX =
  'grok-imagine:landing-auth-return-intent:v1';

export type LandingOptimizationWorkflow =
  | 'text-to-image'
  | 'image-to-image'
  | 'text-to-video'
  | 'image-to-video';

export interface PendingLandingOptimization {
  createdAt: number;
  hasReferenceImages: boolean;
  mode: ComposerMode;
  prompt: string;
  workflow: LandingOptimizationWorkflow;
}

export type LandingLoginIntent = 'generate' | 'optimize';

export function storeLandingAuthReturnIntent(workflow: string) {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(
    getLandingAuthReturnStorageKey(workflow),
    String(Date.now())
  );
}

export function takeLandingAuthReturnIntent(workflow: string) {
  if (typeof sessionStorage === 'undefined') return null;
  const key = getLandingAuthReturnStorageKey(workflow);
  const raw = sessionStorage.getItem(key);
  sessionStorage.removeItem(key);
  if (!raw) return null;
  const startedAt = Number(raw);
  if (
    !Number.isFinite(startedAt) ||
    Date.now() - startedAt > LANDING_AUTH_RETURN_MAX_AGE_MS
  ) {
    return null;
  }
  return startedAt;
}

export function clearLandingAuthReturnIntent(workflow: string) {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.removeItem(getLandingAuthReturnStorageKey(workflow));
}

function getLandingAuthReturnStorageKey(workflow: string) {
  return `${LANDING_AUTH_RETURN_STORAGE_PREFIX}:${workflow}`;
}

export function getLandingLoginCallbackUrl(
  intent: LandingLoginIntent,
  pathname: string,
  generateCallbackUrl: string
) {
  return intent === 'optimize' ? pathname : generateCallbackUrl;
}

export function savePendingLandingOptimization(
  pending: PendingLandingOptimization
) {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(PENDING_OPTIMIZATION_KEY, JSON.stringify(pending));
}

export function takePendingLandingOptimization() {
  if (typeof sessionStorage === 'undefined') return null;
  const raw = sessionStorage.getItem(PENDING_OPTIMIZATION_KEY);
  sessionStorage.removeItem(PENDING_OPTIMIZATION_KEY);
  if (!raw) return null;

  try {
    const pending = JSON.parse(raw) as unknown;
    if (!isPendingLandingOptimization(pending)) return null;
    if (Date.now() - pending.createdAt > PENDING_OPTIMIZATION_MAX_AGE_MS) {
      return null;
    }
    return pending;
  } catch {
    return null;
  }
}

export function clearPendingLandingOptimization() {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.removeItem(PENDING_OPTIMIZATION_KEY);
}

function isPendingLandingOptimization(
  value: unknown
): value is PendingLandingOptimization {
  if (!value || typeof value !== 'object') return false;
  const pending = value as Record<string, unknown>;
  const validWorkflow =
    pending.workflow === 'text-to-image' ||
    pending.workflow === 'image-to-image' ||
    pending.workflow === 'text-to-video' ||
    pending.workflow === 'image-to-video';

  return (
    typeof pending.createdAt === 'number' &&
    typeof pending.hasReferenceImages === 'boolean' &&
    (pending.mode === 'image' || pending.mode === 'video') &&
    typeof pending.prompt === 'string' &&
    pending.prompt.length > 0 &&
    validWorkflow
  );
}
